import { v4 as uuidv4 } from 'uuid'

import { Blot, QuillStatic } from '../../types'

import { EFFECT_BLOT_NAME, EFFECT_TAG_NAME } from './constants'
import { EffectValueForGet, EffectValueForSet } from './types'

const QUILL_STORE: {
  Quill?: QuillStatic
} = {}

export const registerEffectBlot = (Quill: QuillStatic) => {
  QUILL_STORE.Quill = Quill

  const Embed = Quill.import('blots/embed')

  class Effect extends Embed {
    static blotName = EFFECT_BLOT_NAME

    static tagName = EFFECT_TAG_NAME

    static create({
      effectClipId,
      effectId,
      effectName,
      order,
      connected,
    }: EffectValueForSet) {
      const node: HTMLElement = super.create()

      /**
       * 아이디의 사용처는 UI 기능 상에서 선택된 엘리멘트를 찾는데에만 사용됨\
       * 영구적으로 저장될 필요가 없고\
       * 다른 엘리멘트와 중복되지 않고\
       * 매번 새로운 아이디가 할당되어도 문제 없음
       */
      node.id = uuidv4()
      node.setAttribute('effect-clip-id', effectClipId)
      node.setAttribute('effect-id', effectId)
      node.setAttribute('effect-name', effectName)
      node.setAttribute('order', order.toString())
      node.setAttribute('connected', connected.toString())

      /**
       * blot 바로 뒤에 커서가 붙어있을 때, 한글을 입력하면 자모가 분리되는 현상 해결
       */
      node.setAttribute('contenteditable', 'false')

      const orderEl = document.createElement('span')
      const effectNameEl = document.createElement('span')

      orderEl.classList.add('order')
      effectNameEl.classList.add('effect-name')

      orderEl.textContent = (order + 1).toString()

      if (connected) {
        effectNameEl.textContent = 'Effect'
      } else {
        effectNameEl.textContent = 'Not selected'

        node.classList.add('disconnected')
      }

      node.appendChild(orderEl)
      node.appendChild(effectNameEl)

      return node
    }

    static value(domNode: HTMLElement): EffectValueForGet {
      const effectClipId = domNode.getAttribute('effect-clip-id')!
      const effectId = domNode.getAttribute('effect-id')!
      const effectName = domNode.getAttribute('effect-name')!
      const order = parseInt(domNode.getAttribute('order')!)
      const connected = domNode.getAttribute('connected') === 'true'
      const id = domNode.getAttribute('id')!

      return {
        effectClipId,
        effectId,
        effectName,
        order,
        connected,
        id,
      }
    }
  }

  Quill.register(Effect, true)

  return Effect
}

export const findClosestEffectEl = (el: HTMLElement) => {
  return el.closest<HTMLElement>(EFFECT_TAG_NAME)
}

export const findEffectBlot = (
  el: HTMLElement,
):
  | Blot<typeof EFFECT_BLOT_NAME, EffectValueForGet, EffectValueForSet>
  | undefined => {
  const Quill = QUILL_STORE.Quill
  if (!Quill) {
    return
  }

  const closestEl = findClosestEffectEl(el)

  if (!closestEl) {
    return
  }

  return Quill.find(closestEl)
}

export const findEffectBlotById = (id?: string) => {
  if (!id) {
    return
  }

  const el = document.getElementById(id)

  if (!el) {
    return
  }

  return findEffectBlot(el)
}

export const findAllEffectBlotsInQuill = (quill: HTMLElement) => {
  const foundEls = quill.querySelectorAll<HTMLElement>(EFFECT_TAG_NAME)

  const foundBlots = [...foundEls]
    .map((el) => findEffectBlot(el))
    .filter((blot) => !!blot) as Blot<
    typeof EFFECT_BLOT_NAME,
    EffectValueForGet,
    EffectValueForSet
  >[]

  return foundBlots
}
